import { ApolloError, FetchResult } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  CreateScratchpadBucketInput,
  CreateScratchpadBucketMutation,
  ScratchpadBucket,
  ScratchpadBucketScope,
  UpdateScratchpadBucketInput,
  UpdateScratchpadBucketMutation,
  useCreateScratchpadBucketMutation,
  useUpdateScratchpadBucketMutation,
} from '../../../../api/graphql/generated';
import { useGetTenantPreference } from '../../../../api/hooks/preference.hooks';
import { BeaconWeldingData } from '../../beacon/welding/model/beacon-welding-data';
import {
  ApplicationDefaultParameterSetData,
  ApplicationDefaultParameterSetFormTags,
  ApplicationDefaultParameterSetTagData,
  FlowItemForm,
} from '../model/flow.types';
import { useFlowServices } from './flow-service.hooks';

type CreateApplicationDefaultParameterSetInput = Omit<
  CreateScratchpadBucketInput,
  'data'
> & {
  data: ApplicationDefaultParameterSetData;
};

type CreateApplicationDefaultParameterSetResult =
  FetchResult<CreateScratchpadBucketMutation>;

type UpdateApplicationDefaultParameterSetInput = Omit<
  UpdateScratchpadBucketInput,
  'data'
> & {
  id: string;
  data: ApplicationDefaultParameterSetData;
};

type UpdateApplicationDefaultParameterSetResult =
  FetchResult<UpdateScratchpadBucketMutation>;

export type ApplicationDefaultParameterSetFormikFormValues = Pick<
  ScratchpadBucket,
  'name' | 'notes'
> & {
  tags: ApplicationDefaultParameterSetFormTags;
  data: FlowItemForm;
  scope: ScratchpadBucketScope;
};

interface ApplicationDefaultParameterSetService {
  create: [
    (
      bucket: CreateApplicationDefaultParameterSetInput
    ) => Promise<CreateApplicationDefaultParameterSetResult>,
    {
      createError: ApolloError | undefined;
      createLoading: boolean;
    },
  ];
  update: [
    (
      bucket: UpdateApplicationDefaultParameterSetInput
    ) => Promise<UpdateApplicationDefaultParameterSetResult>,
    { updateError: ApolloError | undefined; updateLoading: boolean },
  ];
}

export const useApplicationDefaultParameterSetService =
  (): ApplicationDefaultParameterSetService => {
    const { appSlug } = useFlowServices();

    const [
      createScratchpadBucket,
      { error: createError, loading: createLoading },
    ] = useCreateScratchpadBucketMutation();

    const [
      updateScratchpadBucket,
      { error: updateError, loading: updateLoading },
    ] = useUpdateScratchpadBucketMutation();

    const createApplicationDefaultParameterSet = useCallback(
      async (
        bucket: CreateApplicationDefaultParameterSetInput
      ): Promise<CreateApplicationDefaultParameterSetResult> => {
        const result = await createScratchpadBucket({
          variables: {
            input: {
              scratchpadAppSlug: appSlug,
              ...bucket,
            },
          },
        });

        return result;
      },
      [createScratchpadBucket, appSlug]
    );

    const updateApplicationDefaultParameterSet = useCallback(
      async (
        bucket: UpdateApplicationDefaultParameterSetInput
      ): Promise<UpdateApplicationDefaultParameterSetResult> => {
        const result = await updateScratchpadBucket({
          variables: {
            input: {
              ...bucket,
            },
          },
        });

        return result;
      },
      [updateScratchpadBucket]
    );

    const result: ApplicationDefaultParameterSetService = useMemo(() => {
      return {
        create: [
          createApplicationDefaultParameterSet,
          { createError, createLoading },
        ],
        update: [
          updateApplicationDefaultParameterSet,
          { updateError, updateLoading },
        ],
      };
    }, [
      createError,
      createLoading,
      createApplicationDefaultParameterSet,
      updateError,
      updateLoading,
      updateApplicationDefaultParameterSet,
    ]);

    return result;
  };

interface GetApplicationDefaultParameterSetTagsOptions {
  /**
   * This allows the caller to provide initial tag options based on the tenant preferences.
   * Currently, the welding code needs this to ensure OTC specific tags.
   */
  setInitialTagOptions: (
    tenantOptionPreference: ApplicationDefaultParameterSetTagData | undefined,
    equipment: string
  ) => ApplicationDefaultParameterSetTagData | undefined;
}

export const useGetApplicationDefaultParameterSetTags = (
  options?: GetApplicationDefaultParameterSetTagsOptions
): ApplicationDefaultParameterSetTagData => {
  const { setInitialTagOptions } = options ?? {};
  const { flowService } = useFlowServices();

  const equipment = flowService.getEquipmentName();
  const tenantOptionPreference =
    useGetTenantPreference<BeaconWeldingData.WeldLbraryFormOptionsPreferenceValue>(
      BeaconWeldingData.BeaconWeldingTenantPreferences.LIBRARY_FORM_OPTIONS
    );

  const tags = useMemo<ApplicationDefaultParameterSetTagData>(() => {
    const parameterSetTagToEquipmentMapping =
      flowService.getApplicationDefaultParameterSetTagToEquipmentMapping();

    let parameterSetTagData =
      tenantOptionPreference?.value[equipment] ??
      tenantOptionPreference?.value.default;

    if (typeof setInitialTagOptions === 'function') {
      parameterSetTagData = setInitialTagOptions(
        parameterSetTagData,
        equipment
      );
    }

    const weldParameterSetTags =
      parameterSetTagToEquipmentMapping[equipment] ??
      BeaconWeldingData.InvisionWeldParameterSetTags;

    return Object.entries(weldParameterSetTags).reduce(
      (tags, [key, existingTag]) => {
        if (!isNil(parameterSetTagData) && key in parameterSetTagData) {
          tags[key] = {
            ...existingTag,
            options: [
              ...parameterSetTagData[key].options,
              ...existingTag.options,
            ],
          };
        } else {
          tags[key] = existingTag;
        }

        return tags;
      },
      {} as ApplicationDefaultParameterSetTagData
    );
  }, [
    equipment,
    flowService,
    setInitialTagOptions,
    tenantOptionPreference?.value,
  ]);

  return tags;
};
